import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { utils, writeFile } from 'xlsx';
import { Md5 } from 'ts-md5';
import moment from 'moment';
import { CookieStorageService } from './cookie-storage.service';
import { environment } from '@environment';
import { Options } from 'check-password-strength';
import { FXN_OPERATION_TYPE } from '@sharedModels/enums';
import { v4 as uuidv4 } from 'uuid';
import { table } from 'console';

@Injectable({
  providedIn: 'root'
})

export class UtilService {

  constructor(
    private router: Router,
    private cookieStorageService: CookieStorageService
  ) {}

  /**
   * @description
   * Different page size options for 'mat-paginator'
   */
  pageSizeOptions: number[] = [10, 25, 50]

  /**
   * @description
   * List of image formats supported in app
   */
  acceptedImageFormats: string[] = ['jpeg', 'jpg', 'png']

  /**
   * @description
   * List of files formats supported in app
   */
  acceptedDocFormats: string[] = ['pdf']

  /**
   * @description
   * Custom strength config for 'check-password-strength' npm
   */
  passwordStrengthCustomConfig: Options<any> = [
    {
      id: 0,
      value: "Too weak",
      minDiversity: 0,
      minLength: 0
    }, {
      id: 1,
      value: "Weak",
      minDiversity: 2,
      minLength: 8
    }, {
      id: 2,
      value: "Medium",
      minDiversity: 3,
      minLength: 8
    }, {
      id: 3,
      value: "Strong",
      minDiversity: 4,
      minLength: 12
    }
  ]

  /**
   * Reloads component/page after 500ms
   */
  pageReload = (): void => {
    setTimeout(() => {
      this.router.routeReuseStrategy.shouldReuseRoute = () => false;
      this.router.navigate([this.router.url])
    }, 500)
  }

  /**
   * Converts string to MD5 hash
   *
   * @param value - String to be hashed
   *
   * @returns MD5 string/Int32Array
   */
  md5Hash = (value: string): string | Int32Array => {
    return Md5.hashStr(value)
  }

  /**
   * To parse & remove null values from FormObject
   *
   * @param formObject - Object to be processed for null/empty values or to parse numbers
   * @param excludeKeys - Array of 'keys' which has to be excluded while processing (optional)
   * @param operationType - Operation to be performed 'PARSE_NUMBERS' | 'REMOVE_NULL_VALUES' (default - 'BOTH') (optional)
   *
   * @returns Modified object
   */
  parseAndRemoveNullValues_formObject =(formObject: {}, excludeKeys?: string[], operationType?: FXN_OPERATION_TYPE): {} => {
    Object.keys(formObject).forEach((key: string) => {
      if(!excludeKeys || (excludeKeys && !excludeKeys.includes(key))) {
        if((operationType === 'remove' || !operationType) && formObject[key] === '' || formObject[key] === null || (Array.isArray(formObject[key]) && !formObject[key].length)) {
          delete formObject[key]
        } else if(operationType === 'parse' && formObject[key] === '') {
          formObject[key] = null
        } else if((operationType === 'parse' || !operationType) && !isNaN(+formObject[key]) && typeof formObject[key] !== "boolean" && !Array.isArray(formObject[key])) {
          formObject[key] = +formObject[key]
        }
      }
    })
    return formObject
  }

  /**
   * To parse & remove null values from FormArray
   *
   * @param formArray - Array of objects to be processed for null/empty values or to parse numbers
   * @param excludeKeys - Array of 'keys' which has to be excluded while processing (optional)
   * @param operationType - Operation to be performed 'PARSE_NUMBERS' | 'REMOVE_NULL_VALUES' (default - 'BOTH') (optional)
   *
   * @returns Modified array of objects
   */
  parseAndRemoveNullValues_formArray = (formArray: any[], excludeKeys?: string[], operationType?: FXN_OPERATION_TYPE): any[] => {
    formArray.forEach((formObject: {}) => {
      this.parseAndRemoveNullValues_formObject(formObject, excludeKeys, operationType)
    })
    return formArray
  }

  /**
   * Converts HTML table data into Excel sheet
   *
   * @param tableRef - Reference of HTML table
   * @param fileName - Name of file to be downloaded (without extension*)
   */
  htmlTableToCsv = (tableRef: HTMLTableElement, fileName: string, excludedColumns ?: string[] ): void => {
    
    //Deletes columns passed in excludedColumns array 
    if(excludedColumns){
      let rows = tableRef.rows;
      for (let i = 0; i < rows[0].cells.length; i++) {
        const str = rows[0].cells[i].innerHTML;
        for(let columnName of excludedColumns){
          if (str.search(columnName) != -1) {
            for (let j = 0; j < rows.length; j++)
              rows[j].deleteCell(i);
          }
        }
      }
    }
    const xlsxWorkbook = utils.book_new(),
          xlsxWorksheet = utils.table_to_sheet(tableRef, {display : true})

    utils.book_append_sheet(xlsxWorkbook, xlsxWorksheet)
    writeFile(xlsxWorkbook, fileName.replace(/ /g,'_') + moment()
        .format('_(DD-MM-YYYY_HH.mm.ss)_') + `ap_webApp_${environment.appVersion}.csv`, {bookType: 'csv'})
  }

  /**
   * Converts Array of Arrays into Excel sheet
   *
   * @param arrayOfarrays 
   * @param fileName - Name of file to be downloaded (without extension*)
   */
  arrayOfArraysToCsv = (arrayOfArrays : any[], fileName: string): void => {
    const xlsxWorkbook = utils.book_new(),
          xlsxWorksheet = utils.aoa_to_sheet(arrayOfArrays)

    utils.book_append_sheet(xlsxWorkbook, xlsxWorksheet)
    writeFile(xlsxWorkbook, 'ap_' + fileName.replace(/ /g,'_') + moment().format('_DD-MM-YYYY_hh.mm.ss_a') +
              `_app-${environment.appVersion}.csv`, {bookType: 'csv'})
  }
  /**
   * To get active role of user
   *
   * @returns Current 'active role' of user
   */
  getCurrentActiveRole = (): string => {
    return this.cookieStorageService.getDecodedCookie('webApp_activeRole')
  }

  /**
   * To get email of logged-in user
   *
   * @returns Email of logged-in user
   */
  getCurrentUserEmail = (): string =>{
    return this.cookieStorageService.getDecodedCookie('webApp_userEmail')
  }

  /**
   * Generates 'Universally Unique Identifier' v4 string
   *
   * @return uuidv4 string
   */
  generateUuidV4 = (): string => {
    return uuidv4()
  }

  /**
   * checks whether the site is being loaded inside an iframe 
   * @returns boolean
   */
  static isLoadingInOldPortal = () : boolean => {
    try {
      if (window.self !== window.top) {
        document.documentElement.style.setProperty("--main-color", "#51b6e0")
        document.documentElement.style.setProperty("--dark-color", "#054560")
        document.documentElement.classList.add("old-portal-theme")
        return true
      } else {
        return false
      }
    } catch (e) {
      return false
    }
  }
  
  /**
   * To get details about client system
   *
   * @return clint system details object
   */
  clientDetails(): {
    screenType     : string,
    browserVersion : string,
    userAgent      : string,
    browserName    : string,
    platform       : string,
  } {
    let clientDetail = {
      screenType: window.innerWidth < 768 ? 'Mobile' : 'Desktop',
      browserVersion: navigator.appVersion,
      userAgent: navigator.userAgent,
      browserName: this.detectBrowserName(),
      platform: navigator.platform,
    }
    return clientDetail
  }

  private detectBrowserName() { 
    const agent = window.navigator.userAgent.toLowerCase()
    switch (true) {
      case agent.indexOf('edge') > -1:
        return 'edge';
      case agent.indexOf('opr') > -1 && !!(<any>window).opr:
        return 'opera';
      case agent.indexOf('chrome') > -1 && !!(<any>window).chrome:
        return 'chrome';
      case agent.indexOf('trident') > -1:
        return 'ie';
      case agent.indexOf('firefox') > -1:
        return 'firefox';
      case agent.indexOf('safari') > -1:
        return 'safari';
      default:
        return 'other';
    }
  }
}
